import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "@alm/flow-ds/components";
import { OnApplyProps } from "elements/types";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useStandardElement } from "components/Forms/Element";
import { SelectElement } from "records/Element";
import { useSelector } from "react-redux";
import {
	createGetElementData,
	ElementComponentData,
} from "data/form/elementSelectors";
import { State } from "data";
import { DisplayOnlyValue } from "elements";

function SelectOnApply({
	fid,
	vid,
	id,
	index,
	indexes,
	rowIndex,
	uuid,
	options,
	hint,
	label,
	required,
	disabled,
	displayOnly,
	isApplyClicked,
	fieldsIsEditedStatus,
	setFieldsIsEditedStatus,
}: OnApplyProps<SelectElement>) {
	const { t } = useTranslation();

	const getData = useMemo(createGetElementData, []);
	const selectorProps = useMemo(
		() => ({ fid, vid, id, index, indexes, rowIndex, uuid }),
		[fid, vid, id, index, indexes, rowIndex, uuid]
	);
	const data = useSelector((state: State) =>
		getData(state, selectorProps)
	) as ElementComponentData<string>;
	const { eid, setDataAndQueue } = useStandardElement(selectorProps);

	const [value, setValue] = useState<string>((data && data.value) || "");

	const handleOnChange = useCallback(
		(event: ChangeEvent<HTMLSelectElement>) => {
			setValue(event.target.value as string);
			setFieldsIsEditedStatus(prev => ({ ...prev, [uuid]: true }));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	useEffect(() => {
		if (isApplyClicked && fieldsIsEditedStatus[uuid]) {
			setDataAndQueue(data, {
				id,
				value,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isApplyClicked]);

	// Cover legacy data where values might not be set, or
	// could be set to unknown values. If this is the case
	// we render a disabled option and set it as the
	// selected item
	const ops: JSX.Element[] = [];

	if (value === undefined) {
		ops.push(
			<option value="" key={`${eid}-${undefined}`} disabled>
				{t("elements.select.unselected")}
			</option>
		);
	} else if (options.indexOf(value) === -1) {
		ops.push(
			<option value={value} key={`${eid}-${value}`} disabled>
				{value}
			</option>
		);
	}

	if (options)
		options.forEach((option, index) => {
			ops.push(
				<option value={option} key={`${eid}-${index}`}>
					{option}
				</option>
			);
		});

	// Display only
	if (displayOnly) {
		return <DisplayOnlyValue label={label} value={value || ""} />;
	}

	return (
		<Select
			id={eid}
			onChange={handleOnChange}
			value={(value || "").toString()}
			feedback={data.feedback}
			hint={hint}
			label={label}
			required={required}
			status={data.status}
			disabled={disabled}>
			{ops}
		</Select>
	);
}

export default React.memo(SelectOnApply);
